import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  Chip, Grid, Pagination, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Link
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TimerIcon from '@mui/icons-material/Timer';
import { searchLiveMinutes } from '../services/api';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';

const THEME_COLOR = '#00897b';

// Chamber filter options. Empty value = All. The labels are short for the
// toggle UI but the values are the exact ES keyword values.
const CHAMBER_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'House', value: 'House of Representatives' },
  { label: 'Federation Chamber', value: 'Federation Chamber' },
  { label: 'Senate', value: 'Senate' },
];

// Format a duration in seconds as "Hh MMm" (e.g. 36039 -> "10h 00m"). Handles
// null / negative / non-numeric gracefully.
export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return '—';
  const s = Math.max(0, Math.round(Number(seconds)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

export default function LiveMinutesSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [chamber, setChamber] = useState(searchParams.get('chamber') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [facets, setFacets] = useState({});

  // Date drill-down state
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  // Client-side table sort (the result set is small — one row per chamber-day).
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    handleSearch(searchParams.get('q') || '*', 1, searchParams.get('chamber') || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (
    searchQuery = null,
    newPage = 1,
    chamberVal = chamber,
    year = selectedYear,
    month = selectedMonth,
    pageSize = perPage
  ) => {
    const q = searchQuery !== null ? searchQuery : (query || '*');
    setLoading(true);
    setError(null);
    try {
      const data = await searchLiveMinutes(q, newPage, pageSize, chamberVal || null, year, month);
      setResults(data);
      setPage(newPage);
      if (data.facets) setFacets(data.facets);
      const newParams = {};
      if (q && q !== '*') newParams.q = q;
      if (chamberVal) newParams.chamber = chamberVal;
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      setSearchParams(newParams);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setLoading(false);
    setInitialLoading(false);
  };

  const handleChamberChange = (event, newChamber) => {
    // ToggleButtonGroup yields null when the active button is re-clicked; treat
    // that as "All" rather than leaving an invalid state.
    const value = newChamber === null ? '' : newChamber;
    setChamber(value);
    handleSearch(null, 1, value);
  };

  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, chamber, year, null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    const year = month.split('-')[0];
    setSelectedYear(year);
    handleSearch(null, 1, chamber, year, month);
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, chamber, null, null);
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, chamber, selectedYear, selectedMonth, newSize);
  };

  const handleReset = () => {
    setQuery('');
    setChamber('');
    setResults(null);
    setPage(1);
    setFacets({});
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setSearchParams({});
    handleSearch('*', 1, '');
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder(field === 'date' || field === 'chamber' ? 'asc' : 'desc');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Apply client-side sort to the current page of results.
  const sortedHits = React.useMemo(() => {
    if (!results?.hits) return [];
    const rows = [...results.hits];
    const dir = order === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let av = a[orderBy];
      let bv = b[orderBy];
      if (orderBy === 'date') {
        av = av || '';
        bv = bv || '';
        return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
      }
      if (orderBy === 'chamber') {
        av = av || '';
        bv = bv || '';
        return av.localeCompare(bv) * dir;
      }
      av = Number(av) || 0;
      bv = Number(bv) || 0;
      return (av - bv) * dir;
    });
    return rows;
  }, [results, orderBy, order]);

  const COLUMNS = [
    { id: 'chamber', label: 'Chamber', numeric: false },
    { id: 'date', label: 'Date', numeric: false },
    { id: 'item_count', label: 'Items', numeric: true },
    { id: 'program_item_count', label: 'Business items', numeric: true },
    { id: 'total_duration_seconds', label: 'Sitting duration', numeric: true },
  ];

  const hasDateFacets = facets.years?.length > 0 || facets.months?.length > 0 || facets.days?.length > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TimerIcon sx={{ color: THEME_COLOR, fontSize: 32 }} />
        <Typography variant="h4">Live Minutes</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Draft running minutes for each chamber's sitting day, with the time spent on every business item.
      </Typography>

      <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            placeholder="Search Live Minutes..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 }, minWidth: 240 }}
          />
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ borderRadius: 3, px: 4, bgcolor: THEME_COLOR, '&:hover': { bgcolor: '#00695c' } }}
          >
            Search
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
            Reset
          </Button>
        </Box>
        <ToggleButtonGroup
          value={chamber}
          exclusive
          onChange={handleChamberChange}
          size="small"
          sx={{ flexWrap: 'wrap' }}
        >
          {CHAMBER_OPTIONS.map((opt) => (
            <ToggleButton
              key={opt.value || 'all'}
              value={opt.value}
              sx={{
                textTransform: 'none',
                '&.Mui-selected': { bgcolor: THEME_COLOR, color: 'white', '&:hover': { bgcolor: '#00695c' } }
              }}
            >
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      {initialLoading && <SearchSkeleton count={4} showFacets={true} />}

      {!initialLoading && error && (
        <ErrorState message={error} onRetry={() => handleSearch()} themeColor={THEME_COLOR} />
      )}

      {!initialLoading && !error && results && results.total === 0 && (
        <EmptyState
          query={query}
          type="live minutes"
          onSuggestionClick={(q) => { setQuery(q); handleSearch(q); }}
          onReset={handleReset}
          themeColor={THEME_COLOR}
        />
      )}

      {!initialLoading && !error && results && results.total > 0 && (
        <Grid container spacing={3}>
          {hasDateFacets && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Filter Results</Typography>
                  {selectedYear && (
                    <Button size="small" onClick={clearDateDrillDown}>Clear</Button>
                  )}
                </Box>
                <DateHierarchyFilter
                  facets={facets}
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                  selectedDay={selectedDay}
                  onYearClick={handleYearClick}
                  onMonthClick={handleMonthClick}
                  onDayClick={handleDayClick}
                  onClearAll={clearDateDrillDown}
                  themeColor={THEME_COLOR}
                />
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={hasDateFacets ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1">
                    {results.total.toLocaleString()} sitting {results.total === 1 ? 'day' : 'days'} found
                  </Typography>
                  <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                </Box>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>

              <TableContainer>
                <Table size="small" aria-label="Live Minutes results">
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((col) => (
                        <TableCell
                          key={col.id}
                          align={col.numeric ? 'right' : 'left'}
                          sortDirection={orderBy === col.id ? order : false}
                          sx={{ fontWeight: 600 }}
                        >
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : 'asc'}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedHits.map((hit) => (
                      <TableRow key={hit.id} hover>
                        <TableCell>
                          <Chip label={hit.chamber} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={`/live-minutes/${encodeURIComponent(hit.id)}`}
                            underline="hover"
                            sx={{ color: THEME_COLOR, fontWeight: 500 }}
                          >
                            {formatDate(hit.date) || hit.date}
                          </Link>
                        </TableCell>
                        <TableCell align="right">{hit.item_count ?? '—'}</TableCell>
                        <TableCell align="right">{hit.program_item_count ?? '—'}</TableCell>
                        <TableCell align="right">{formatDuration(hit.total_duration_seconds)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
